import { useEffect, useRef, useState } from 'react';
import '../styles/elements.css';
import {
  CASE_DOMAINS,
  ELEMENTS_OPTIONAL_NOTE,
  ELEMENT_LABELS,
  ELEMENT_MAXLEN,
  editElement,
  elementFilled,
  elementsHint,
  originBadge,
} from '../util/complaint-rules';
import type { CaseElements as CaseElementsValue, ElementKey, ElementOrigins } from '../util/complaint-rules';
import type { AreaOption, PrefillStatus } from '@cdfjx/shared/dist/prefill-contract';
import { RegionPicker } from './RegionPicker';

/**
 * 确认页上的「案件要素」块 —— **手机端与 PC 端同一个组件**。
 *
 * ★ 为什么合成一个而不是两端各画一套：这五项的规矩（哪五项、每项叫什么、
 *   AI 给的与市民改的怎么分、区域为什么只能选、事由为什么只改概括不改原文）
 *   条条都容易在第二份实现里走样，而走样的表现是「两个入口交上来的东西不一样」——
 *   本项目在契约与校验上都吃过分叉的亏，这里不再开第三处。
 *   两端的差别只有**版式**，由 `variant` 一个 class 控制，不写第二套逻辑。
 *
 * ★ 三条红线（改这个文件之前先读）：
 *   ① **判不准就留空，绝不编造。** 没有默认选中、没有占位值、没有「其他」兜底。
 *   ② **绝不挡住提交。** 这一块里没有任何东西能让提交按钮变灰；
 *      五项全空是完全合法的状态，界面还要主动说一句「留空也能提交」。
 *   ③ **只标数据来源，不给用户贴标签。** 徽标只有「AI 判定／AI 摘要／AI 提取／已修改」。
 *
 * ★ 区域是 `<select>`，**不是**可输入的下拉：`区域ID` 要推协会老系统，
 *   填表外的值推送当场失败且删不掉（见 `hooks/useAreas.ts` 文件头）。
 *   拉不到表时禁用并说清楚，**不回落成写死的 24 条**。
 *
 * ★ 「投诉事由」那一行有一个显式的「修改」入口，其余几行直接就是输入框。
 *   这不是不一致 —— 它是**故意的**：那一句是 AI 对市民原文的概括，
 *   点「修改」时会明说「改的是这一句概括，您写的投诉内容及诉求不会动」。
 *   原文是市民自己写的原始证据，**不给回去改**（主公定）。
 */
/**
 * 这一块要的**全部**入参（不含 `variant`）。
 *
 * 单独抽成一个类型，是为了让两个确认页把它当**一件东西**原样传下去：
 * 十来个 prop 各写一遍，迟早有一端漏掉一个（漏掉 `disabled` 就是提交半路还能改字段），
 * 而漏掉时 TypeScript 只会在那一端报错、另一端照样绿。
 */
export interface CaseElementsPanel {
  value: CaseElementsValue;
  origins: ElementOrigins;
  onChange: (next: CaseElementsValue, nextOrigins: ElementOrigins) => void;
  /** 正在识别中。正常路径下市民看不到（1.6 秒里他还在填别的），填得飞快时会看到 */
  running: boolean;
  status: PrefillStatus | null;
  /** `null` ＝ 区域表还没拉到。空数组与 null 不是一回事 */
  areas: AreaOption[] | null;
  areasLoading: boolean;
  areasError: string;
  onReloadAreas: () => void;
  /** 提交中：整块只读，免得他在落库的半路上改一个字 */
  disabled: boolean;
}

/**
 * 一行的标题 ＋ 来源徽标。
 *
 * 定在模块顶层而不是组件里面：定在里面的话每次渲染都是一个**新的组件类型**，
 * React 会把这一小棵子树整个卸掉重建 —— 这里只有两个 span 还看不出毛病，
 * 但那是个坏样板，抄到带输入框的地方就会变成「打一个字掉一次焦点」。
 *
 * ★ 徽标只标**数据从哪来**（AI 判定／AI 摘要／AI 提取／已修改），不描述用户本人。
 */
function RowHead({ k, origins }: { k: ElementKey; origins: ElementOrigins }) {
  const badge = originBadge(k, origins[k]);
  return (
    <div className="el-k">
      <span className="el-label">{ELEMENT_LABELS[k]}</span>
      {badge ? <span className={origins[k] === 'user' ? 'el-tag el-tag--user' : 'el-tag'}>{badge}</span> : null}
    </div>
  );
}

export default function CaseElements({
  value,
  origins,
  onChange,
  running,
  status,
  areas,
  areasLoading,
  areasError,
  onReloadAreas,
  disabled,
  variant,
}: CaseElementsPanel & { variant: 'phone' | 'pc' }) {
  /** 「投诉事由」是否处在编辑态。默认否 —— 先让他读那一句，读着不对再点「修改」 */
  const [editingMatter, setEditingMatter] = useState(false);
  const matterRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingMatter) matterRef.current?.focus();
  }, [editingMatter]);

  const hint = elementsHint(status, value, running);

  const put = (key: ElementKey, patch: Partial<CaseElementsValue>) => {
    const next = editElement(value, origins, key, patch);
    onChange(next.elements, next.origins);
  };

  const Head = ({ k }: { k: ElementKey }) => <RowHead k={k} origins={origins} />;

  return (
    <section className={variant === 'pc' ? 'el-block el-block--pc' : 'el-block'}>
      <div className="el-h">
        <span className="el-bar" />
        <h3>案件要素</h3>
        <span className="el-sp" />
        {running ? (
          <span className="el-busy" role="status">
            <span className="el-spin" aria-hidden="true" />
            正在识别…
          </span>
        ) : (
          <span className="el-side">请核对，可修改</span>
        )}
      </div>

      {/* ★ 2026-08-27 主公令：**不留「重新识别」**。
          没跑出来时，市民直接自己填这几项即可，比等一次重算快得多；
          摆一个重试钮反而引他去等、去反复按，而每按一次都真花钱打一次模型
          （这条链路目前无限流）。提示只说"可以自己填、留空也能提交"。 */}
      {hint ? <p className="el-hint">{hint}</p> : null}

      <div className="el-rows">
        {/* ── 案件领域：四选一。横排标签，选中的那个高亮，再点一下取消 ───────── */}
        <div className="el-row">
          <Head k="domain" />
          <div className="el-v">
            <div className="el-chips" role="group" aria-label="案件领域">
              {CASE_DOMAINS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={value.案件领域 === d ? 'el-chip el-chip--on' : 'el-chip'}
                  aria-pressed={value.案件领域 === d}
                  disabled={disabled}
                  onClick={() => put('domain', { 案件领域: value.案件领域 === d ? '' : d })}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── 区域：只能从协会那张表里选，不许自由输入 ───────────────────── */}
        <div className="el-row">
          <Head k="region" />
          <div className="el-v">
            {/* ★ 2026-09-06：原生 <select> → 抽屉式选择器（主公「优化一下」）。
                24 个区县塞在系统滚轮里，一次只看得见三四条，找「都江堰市」要盲滚半天；
                而同页的「被投诉方」用的是抽屉，两个控件长得不是一路。
                **硬约束没松**：仍然只能从协会那张表里选，没有任何可提交的自由文本
                （搜索框只过滤本地列表）。理由见 elements.css 里那段与 RegionPicker 文件头。 */}
            <RegionPicker
              areas={areas}
              valueId={value.区域ID}
              valueName={value.区域名}
              disabled={disabled || areas === null}
              onPick={(a) => put('region', { 区域ID: a.id, 区域名: a.name })}
              onClear={() => {
                /* 清空＝把这一项整个清掉。**不留一个孤零零的区域名**，
                   那会变成「界面上有字、提交体里没值」的假状态。 */
                put('region', { 区域ID: null, 区域名: '' });
              }}
            />

            {/* 表没拉到：说清楚 ＋ 给重试 ＋ 明说不影响提交。**不回落成写死的 24 条** */}
            {areas === null ? (
              <p className="el-note">
                {areasLoading ? (
                  '正在读取可选区域…'
                ) : (
                  <>
                    {areasError || '暂时读不到可选区域'}
                    <button type="button" className="el-link" onClick={onReloadAreas} disabled={disabled}>
                      重试
                    </button>
                    {/* 这句承诺全站只有一处出处，见 complaint-rules.ts 的
                        ELEMENTS_OPTIONAL_NOTE（区域若改必选，改那一处就够） */}
                    <span className="el-note-tail">{ELEMENTS_OPTIONAL_NOTE}。</span>
                  </>
                )}
              </p>
            ) : null}

            {/* 识别出了区名、却对不上表：把线索留给他自己确认，绝不替他挑一个 */}
            {areas !== null && value.区域ID === null && value.区域名 ? (
              <p className="el-note">识别到「{value.区域名}」，请在上面的列表里确认。</p>
            ) : null}
          </div>
        </div>

        {/* ── 房产标的：单行文本，可空 ─────────────────────────────────── */}
        <div className="el-row">
          <Head k="property" />
          <div className="el-v">
            <input
              className="el-in"
              type="text"
              aria-label="房产标的"
              maxLength={ELEMENT_MAXLEN.property}
              value={value.房产标的}
              disabled={disabled}
              placeholder="涉及的楼盘 / 门牌 / 房号，没有可留空"
              onChange={(e) => put('property', { 房产标的: e.target.value })}
            />
          </div>
        </div>

        {/* ── 投诉事由：先摆出来给他读，点「修改」才进编辑态 ─────────────── */}
        <div className="el-row">
          <Head k="matter" />
          <div className="el-v">
            {editingMatter ? (
              <>
                <div className="el-inline">
                  <input
                    ref={matterRef}
                    className="el-in"
                    type="text"
                    aria-label="投诉事由"
                    maxLength={ELEMENT_MAXLEN.matter}
                    value={value.投诉事项}
                    disabled={disabled}
                    placeholder={`一句话说清投诉的是什么事，不超过 ${ELEMENT_MAXLEN.matter} 字`}
                    onChange={(e) => put('matter', { 投诉事项: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setEditingMatter(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="el-link el-link--done"
                    onClick={() => setEditingMatter(false)}
                  >
                    完成
                  </button>
                </div>
                {/* 这一句必须说：他点「修改」时最容易以为自己在改投诉内容及诉求 */}
                <p className="el-note">改的是这一句概括，您写的投诉内容及诉求原文不会改动。</p>
              </>
            ) : (
              <div className="el-inline">
                <span className={elementFilled(value, 'matter') ? 'el-text' : 'el-text el-text--blank'}>
                  {value.投诉事项 || '未填写'}
                </span>
                <button
                  type="button"
                  className="el-link"
                  disabled={disabled}
                  onClick={() => setEditingMatter(true)}
                >
                  修改
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── 诉求：单行文本 ───────────────────────────────────────────── */}
        <div className="el-row">
          <Head k="demand" />
          <div className="el-v">
            <input
              className="el-in"
              type="text"
              aria-label="诉求"
              maxLength={ELEMENT_MAXLEN.demand}
              value={value.诉求}
              disabled={disabled}
              placeholder={`一句话说清您希望怎么解决，不超过 ${ELEMENT_MAXLEN.demand} 字`}
              onChange={(e) => put('demand', { 诉求: e.target.value })}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
